import styled from "styled-components";
import React, { useCallback, useEffect, useState } from "react";
import SettingsDropDown from "./SettingsDropDown";
import SettingsItem from "./SettingsItem";
import { Flex } from "./shared/Flex";
import { Sex } from "@binah/web-sdk";
import { useContactInfo } from "../context/ContactInfoContext";
import { useSubjectDemographic } from "../context/SubjectDemographicContext"; // Import the context hook

// styles

const Container = styled(Flex)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 0px;
  flex-direction: column;
`;

const Demographic = styled.div`
  margin-top: 15px;
  display: block;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f1f4f9;
  margin-top: 15px;
`;

const Label = styled.h3`
  font-style: normal;
  font-weight: normal;
  font-size: 1em;
  line-height: 19px;
  color: #3e3c3c;
  margin-bottom: 6px;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 2rem;
  font-size: 2.5rem;
  text-align: center;
`;

const HelpBlock = styled.div`
  color: #d80000;
  font-size: 0.9em;
  line-height: 14px;
  margin-top: 5px;
  margin-left: 2px;
`;

const FormPage = ({
  onSettingsComplete,
}: {
  onSettingsComplete: () => void;
}) => {
  //variables

  const [isNameValid, setIsNameValid] = useState<boolean>(false);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<boolean>(false);
  const [phoneNumberMessage, setPhoneNumberMessage] = useState("");
  const [isAgeValid, setIsAgeValid] = useState<boolean>(true);
  const [isHeightValid, setIsHeightValid] = useState<boolean>(true);
  const [isWeightValid, setIsWeightValid] = useState<boolean>(true);
  const [demographicsValid, setDemographicsValid] = useState<boolean>(false);
  const { subjectDemographic, setSubjectDemographic } = useSubjectDemographic();
  const { contactInfo, setContactInfo } = useContactInfo();

  // hooks

  // Initialize subjectDemographic with default values if empty
  useEffect(() => {
    if (!subjectDemographic || !Object.keys(subjectDemographic).length) {
      setSubjectDemographic({
        age: 0,
        sex: Sex.MALE,
        height: 0,
        weight: 0,
      });
    }
  }, []);

  useEffect(() => {
    // Check if all demographic fields are valid
    const isDemographicsValid: boolean =
      isAgeValid && isHeightValid && isWeightValid;

    setDemographicsValid(isDemographicsValid);
  }, [isAgeValid, isHeightValid, isWeightValid, subjectDemographic]);

  useEffect(() => {
    // Call onSettingsComplete when all required settings are valid
    if (demographicsValid) {
      onSettingsComplete();
    }
  }, [demographicsValid, onSettingsComplete]);

  // validators

  const validateAge = useCallback((age: number) => {
    const isValid = age >= 18 && age <= 110;
    setIsAgeValid(isValid);
    return isValid;
  }, []);

  const validateHeight = useCallback((height: number): boolean => {
    const isValid = height >= 130 && height <= 230;
    setIsHeightValid(isValid);
    return isValid;
  }, []);

  const validateWeight = useCallback((weight: number): boolean => {
    const isValid = weight >= 40 && weight <= 200;
    setIsWeightValid(isValid);
    return isValid;
  }, []);

  const validateName = useCallback((name: string): boolean => {
    const isValid = name.length > 0;
    setIsNameValid(isValid);
    return isValid;
  }, []);

  const validatePhoneNumber = useCallback((phoneNumber: string): boolean => {
    const venezuelanPhoneRegex = /^0(412|414|416|424|426|2\d{2})\d{7}$/;
    // Clean the phone number: remove all non-digit characters
    const cleanedNumber = phoneNumber.replace(/\D/g, "");

    let isValid = false;
    if (cleanedNumber.length === 0) {
      setPhoneNumberMessage("Ingrese un número de teléfono (11 dígitos).");
    } else if (cleanedNumber.length < 11) {
      setPhoneNumberMessage(
        "El número debe tener 11 dígitos. Faltan " +
          (11 - cleanedNumber.length) +
          " dígitos.",
      );
    } else if (cleanedNumber.length > 11) {
      setPhoneNumberMessage(
        "El número excede los 11 dígitos. Sobran " +
          (cleanedNumber.length - 11) +
          " dígitos.",
      );
    } else if (venezuelanPhoneRegex.test(cleanedNumber)) {
      setPhoneNumberMessage("Número válido. ¡Excelente!");
      isValid = true;
    } else {
      isValid = false;
      setPhoneNumberMessage(
        "Formato de número inválido. Ej: 04121234567 o 02121234567.",
      );
    }

    setIsPhoneNumberValid(isValid);
    return isValid;
  }, []);

  // handlers

  const handleAgeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const age = value ? parseInt(value) : 0;
      validateAge(age);
      setSubjectDemographic((prev) => ({
        ...prev,
        age: age,
      }));
    },
    [setSubjectDemographic, validateAge],
  );

  const handleHeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const height = value ? parseInt(value) : 0;
      validateHeight(height);
      setSubjectDemographic((prev) => ({
        ...prev,
        height: height,
      }));
    },
    [setSubjectDemographic, validateHeight],
  );

  const handleWeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const weight = value ? parseInt(value) : 0;
      validateWeight(weight);
      setSubjectDemographic((prev) => ({
        ...prev,
        weight: weight,
      }));
    },
    [setSubjectDemographic, validateWeight],
  );

  const handleSexChange = useCallback(
    (value: Sex) => {
      setSubjectDemographic((prev) => ({ ...prev, sex: value }));
    },
    [setSubjectDemographic],
  );

  const handlePhoneNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value || "";
      validatePhoneNumber(value);
      setContactInfo((prev) => ({...prev, phone: value}))
    },
    [validatePhoneNumber, setContactInfo],
  );

  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value || "";
      validateName(value);
      setContactInfo((prev) => ({ ...prev, name: value }));
    },
    [validateName, setContactInfo],
  );

  // JSX

  return (
    <div id="formData">
      <Container>
        <Title>Datos personales</Title>
        <Demographic>
          <SettingsItem
            title={"Nombre y apellido:"}
            type={"string"}
            value={contactInfo.name}
            onChange={handleNameChange}
            onBlur={() => validateName(contactInfo.name)}
            isValid={isNameValid}
            placeholder="José Pérez"
            maxLength={40}
            autoFocus={true}
          />
          {!isNameValid && <HelpBlock>El nombre es requerido</HelpBlock>}
          <Separator />
          <SettingsItem
            title={"Teléfono celular:"}
            type={"string"}
            value={contactInfo.phone}
            onChange={handlePhoneNumberChange}
            onBlur={() => validatePhoneNumber(contactInfo.phone)}
            isValid={isPhoneNumberValid}
            placeholder="04125551234"
            maxLength={11}
          />
          {!isPhoneNumberValid && <HelpBlock>{phoneNumberMessage}</HelpBlock>}
          <Separator />
          <Label>Sexo:</Label>
          <SettingsDropDown
            onSelect={handleSexChange}
            options={[
              { value: Sex.MALE, name: "Masculino" },
              { value: Sex.FEMALE, name: "Femenino" },
              { value: Sex.UNSPECIFIED, name: "Sin especificar" },
            ]}
            value={subjectDemographic.sex}
          />
          <Separator />
          <SettingsItem
            title={"Edad (años):"}
            type={"number"}
            value={subjectDemographic.age}
            onChange={handleAgeChange}
            onBlur={() => validateAge(subjectDemographic.age)}
            isValid={isAgeValid}
          />
          {!isAgeValid && <HelpBlock>Valores válidos: 18-110</HelpBlock>}
          <Separator />
          <SettingsItem
            title={"Peso (kg):"}
            type={"number"}
            value={subjectDemographic.weight}
            onChange={handleWeightChange}
            onBlur={() => validateWeight(subjectDemographic.weight)}
            isValid={isWeightValid}
          />
          {!isWeightValid && <HelpBlock>Valores válidos: 40-200</HelpBlock>}
          <Separator />
          <SettingsItem
            title={"Altura (cm):"}
            type={"number"}
            value={subjectDemographic.height}
            onChange={handleHeightChange}
            onBlur={() => validateHeight(subjectDemographic.height)} // Pass the current height value
            isValid={isHeightValid}
          />
          {!isHeightValid && <HelpBlock>Valores válidos: 130-230</HelpBlock>}
          <Separator />
        </Demographic>
      </Container>
    </div>
  );
};

export default FormPage;
